import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientRequests = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/client/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/client/requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request submitted successfully!');
      setFormData({ title: '', description: '' });
      await fetchRequests();
      setOpen(false);
    } catch (error) {
      console.error('Failed to create request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 text-green-400';
      case 'in_progress': return 'bg-blue-500/10 text-blue-400';
      default: return 'bg-yellow-500/10 text-yellow-400';
    }
  };

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="client-requests-page">
      <ClientNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="requests-title">Requests</h1>
            <p className="text-slate-400">Submit and track your project requests</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-black hover:bg-slate-200" data-testid="new-request-btn">
                <Plus size={18} className="mr-2" />
                New Request
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-950 border-slate-800 text-white">
              <DialogHeader>
                <DialogTitle>New Request</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Submit a new change request or feature request
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Title</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Brief description of the request"
                    required
                    className="bg-slate-900 border-slate-800 text-white"
                    data-testid="request-title-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Detailed description of what you need..."
                    required
                    rows={6}
                    className="bg-slate-900 border-slate-800 text-white resize-none"
                    data-testid="request-description-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
                  data-testid="submit-request-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {requests.length === 0 ? (
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center" data-testid="no-requests">
            <MessageSquare className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No requests yet</h3>
            <p className="text-slate-400 mb-4">Create your first request to get started</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="requests-list">
            {requests.map((request) => (
              <Link
                key={request.id}
                to={`/client/requests/${request.id}`}
                className="block bg-slate-950 border border-slate-800 rounded-xl p-6 hover:border-indigo-500/50 transition-colors"
                data-testid={`request-item-${request.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-semibold text-white">{request.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                    {request.status.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-slate-400 mb-4 line-clamp-2">{request.description}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">
                    {format(new Date(request.created_at), 'MMM dd, yyyy')}
                  </span>
                  <span className="text-indigo-400 flex items-center space-x-1">
                    <MessageSquare size={16} />
                    <span>{request.messages?.length || 0} messages</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientRequests;
